// Preset picker for the "choose from preset" planning path and the trip
// Presets tab: destination-specific day blocks from DAY_PRESETS in data.js
// (getPresetsForDest). Selecting presets feeds selectedPresets/selectedIdeas
// in App.js, which builds the itinerary from the preset's idea ids.
import React from 'react';
import { PALETTE, headerPad } from '../theme';
import { Icon, IconBtn, BigButton, Patch } from '../components/UI';
import { getPresetsForDest } from '../data';

export function PresetsScreen({ isMobile, destination, onBack, onContinue, accent = PALETTE.accent, selectedPresets, setSelectedPresets, selectedIdeas = [], setSelectedIdeas, destIdeas = [] }) {
  const presets = getPresetsForDest(destination);
  const [expanded, setExpanded] = React.useState(null);

  const toggle = (preset) => {
    const allIds = preset.days.flatMap(d => d.ideas);
    if (selectedPresets.includes(preset.id)) {
      setSelectedPresets(prev => prev.filter(x => x !== preset.id));
      setSelectedIdeas(prev => prev.filter(x => !allIds.includes(x)));
    } else {
      setSelectedPresets(prev => [...prev, preset.id]);
      setSelectedIdeas(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const totalIdeasCount = new Set(
    presets.filter(p => selectedPresets.includes(p.id)).flatMap(p => p.days.flatMap(d => d.ideas))
  ).size;

  const ideaMap = React.useMemo(() => {
    const m = {};
    destIdeas.forEach(i => { m[i.id] = i; });
    return m;
  }, [destIdeas]);

  return (
    <div style={{ background: PALETTE.paper, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 4, background: PALETTE.paper,
        padding: headerPad(isMobile), borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 36 }}>
          <IconBtn name="arrowL" label="Back" onClick={onBack} />
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase' }}>
            {destination ? destination.split(',')[0] : 'New trip'} · Presets
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 24px 4px' }}>
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 28, letterSpacing: -0.6, color: PALETTE.ink, lineHeight: 1.1 }}>
          Pick a day template.
        </div>
        <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginTop: 8, lineHeight: 1.5 }}>
          Each preset is a curated day-by-day block. You can combine them and edit everything later.
        </div>
      </div>

      {/* Counter */}
      {selectedPresets.length > 0 && (
        <div style={{
          margin: '16px 20px 4px', padding: '10px 14px',
          background: PALETTE.card, borderRadius: 10,
          border: `0.5px solid ${PALETTE.ruleSoft}`,
          fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkSoft, letterSpacing: 0.5,
        }}>
          {selectedPresets.length} preset{selectedPresets.length > 1 ? 's' : ''} · {totalIdeasCount} activities selected
        </div>
      )}

      <div style={{ flex: 1, padding: '12px 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {presets.length === 0 ? (
          <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, textAlign: 'center', padding: '40px 0' }}>
            No presets available for this destination yet.
          </div>
        ) : presets.map(p => (
          <DayPresetCard
            key={p.id}
            preset={p}
            selected={selectedPresets.includes(p.id)}
            expanded={expanded === p.id}
            onToggle={() => toggle(p)}
            onExpand={() => setExpanded(expanded === p.id ? null : p.id)}
            accent={accent}
            ideaMap={ideaMap}
          />
        ))}
      </div>

      <div style={{
        position: 'sticky', bottom: 0,
        background: 'linear-gradient(to top, rgba(244,239,230,1) 60%, rgba(244,239,230,0))',
        padding: '20px 20px 28px', display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <BigButton
          onClick={onContinue}
          disabled={selectedPresets.length === 0}
          style={{ background: selectedPresets.length > 0 ? accent : PALETTE.rule, color: '#fff' }}
        >
          {selectedPresets.length === 0 ? 'Select a preset above' : <><span>Build my itinerary</span> <Icon name="arrow" size={18} /></>}
        </BigButton>
        {selectedPresets.length === 0 && (
          <button onClick={onContinue} style={{ appearance: 'none', border: 0, background: 'transparent', fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, cursor: 'pointer', padding: '6px 0', textAlign: 'center' }}>
            Skip — I'll customise later
          </button>
        )}
      </div>
    </div>
  );
}

function DayPresetCard({ preset, selected, expanded, onToggle, onExpand, accent, ideaMap }) {
  const totalActivities = preset.days.flatMap(d => d.ideas).length;
  return (
    <div style={{
      background: PALETTE.card, borderRadius: 14, overflow: 'hidden',
      border: selected ? `2px solid ${accent}` : `0.5px solid ${PALETTE.ruleSoft}`,
      transition: 'border 0.15s',
    }}>
      {/* Card header row */}
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <Patch swatch={preset.swatch} height={100} style={{ width: 80, flexShrink: 0, borderRadius: 0 }} />
        <div style={{ flex: 1, padding: '12px 12px 12px 14px', position: 'relative', minWidth: 0 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1.2, textTransform: 'uppercase' }}>
            {preset.kicker}
          </div>
          <div style={{ fontFamily: 'Fraunces', fontSize: 17, letterSpacing: -0.3, color: PALETTE.ink, marginTop: 3, lineHeight: 1.2, paddingRight: 28 }}>
            {preset.title}
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginTop: 4, lineHeight: 1.4 }}>
            {preset.body}
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.5 }}>
              {preset.days.length} day{preset.days.length > 1 ? 's' : ''} · {totalActivities} activities
            </span>
            <button
              onClick={onExpand}
              style={{ appearance: 'none', border: 'none', background: 'transparent', padding: '2px 0', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: accent, display: 'flex', alignItems: 'center', gap: 3 }}
            >
              {expanded ? 'Hide' : 'See days'}
              <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={11} />
            </button>
          </div>
          <button
            onClick={onToggle}
            style={{
              position: 'absolute', top: 10, right: 10, appearance: 'none', cursor: 'pointer',
              width: 22, height: 22, borderRadius: 999,
              border: selected ? 'none' : `1.5px solid ${PALETTE.rule}`,
              background: selected ? accent : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
              padding: 0,
            }}
          >
            {selected && <Icon name="check" size={13} stroke={2.5} />}
          </button>
        </div>
      </div>

      {/* Expanded day breakdown */}
      {expanded && (
        <div style={{ borderTop: `0.5px solid ${PALETTE.ruleSoft}`, padding: '10px 14px 12px' }}>
          {preset.days.map((day, i) => (
            <div key={i} style={{ marginBottom: i < preset.days.length - 1 ? 10 : 0 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>
                Day {i + 1} — {day.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {day.ideas.map(id => {
                  const idea = ideaMap[id];
                  if (!idea) return null;
                  return (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 4, height: 4, borderRadius: 2, background: PALETTE.inkMute, flexShrink: 0 }} />
                      <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>{idea.title}</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, marginLeft: 'auto', flexShrink: 0 }}>{idea.meta?.split('·')[1]?.trim() || ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
